import { api } from '@/services/api';
import { isCompanyApplicationsWarmScope, type CompanyApplicationsListScope } from '@/lib/companyApplicationsList';
import type { Application, JobPosition } from '@/types';

const DEFAULT_MAX_POSITIONS = 16;
const DEFAULT_HEAD_LIMIT = 40;
const DEFAULT_CONCURRENCY = 4;

/**
 * Background-fetch the top of each position list (same data as Job Position → Applications)
 * and patch position caches so company Applications sort is correct without visiting each job.
 */
export async function warmPositionApplicationHeads(
  companyId: string,
  positions: JobPosition[],
  onPatch: (apps: Application[]) => void,
  options?: {
    maxPositions?: number;
    headLimit?: number;
    concurrency?: number;
  },
): Promise<void> {
  const maxPositions = options?.maxPositions ?? DEFAULT_MAX_POSITIONS;
  const headLimit = options?.headLimit ?? DEFAULT_HEAD_LIMIT;
  const concurrency = options?.concurrency ?? DEFAULT_CONCURRENCY;

  const targets = positions
    .filter((p) => p.status === 'active' || p.status === 'closed')
    .slice(0, maxPositions);

  const merged: Application[] = [];
  for (let i = 0; i < targets.length; i += concurrency) {
    const chunk = targets.slice(i, i + concurrency);
    const pages = await Promise.all(
      chunk.map((p) =>
        api.applications
          .listByPositionPage(companyId, p.id, {
            archived: false,
            orderBy: 'updatedAt',
            limit: headLimit,
          })
          .catch(() => [] as Application[]),
      ),
    );
    for (const page of pages) {
      merged.push(...page);
    }
  }

  if (merged.length > 0) {
    onPatch(merged);
  }
}

export function shouldWarmPositionHeads(scope: CompanyApplicationsListScope): boolean {
  return isCompanyApplicationsWarmScope(scope);
}
